// ServiceDetailPage - Updated to use direct subservices fetching (v2)
// Fixed: Removed allSubservices and memoizedSubservices references
import React, { useMemo, memo, useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useServices } from '../hooks/useAirtable';
import ServiceDetailContent from '../components/ServiceDetailContent';
import ServiceDetailGallery from '../components/ServiceDetailGallery';
import ServiceDetailSidebar from '../components/ServiceDetailSidebar';
import Footer from '../components/Footer';
import Header from '../components/Header';
import nocobaseService from '../services/nocobaseService';
import { sanitizeRichText } from '../utils/sanitize';

// Helper function to strip HTML tags and decode HTML entities
const stripHtmlTags = (html) => {
  if (!html || typeof html !== 'string') return '';
  
  return html
    // Remove all HTML tags
    .replace(/<[^>]*>/g, '')
    // Decode HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    // Clean up extra whitespace
    .replace(/\s+/g, ' ')
    .trim();
};

// Memoized components to prevent unnecessary re-renders
const MemoizedHeader = memo(Header);
const MemoizedFooter = memo(Footer);
const MemoizedSidebar = memo(ServiceDetailSidebar);

const ServiceDetailPage = ({ onOpenChat, contentOnly = false }) => {
  const { id, lang } = useParams();
  const navigate = useNavigate();
  const { currentLanguage, isRTL, changeLanguage } = useLanguage();
  
  // Determine the language to use - prioritize URL lang over context
  const activeLanguage = (lang && (lang === 'ar' || lang === 'en')) ? lang : currentLanguage;
  const activeIsRTL = activeLanguage === 'ar';
  
  // Update language from URL if provided, or redirect to language-specific URL
  useEffect(() => {
    if (!id) return;
    
    if (lang && (lang === 'ar' || lang === 'en')) {
      // Language is in URL, update context if different
      if (lang !== currentLanguage) {
        changeLanguage(lang);
      }
    } else {
      // No language in URL, redirect to include language (only once)
      if (currentLanguage) {
        navigate(`/${currentLanguage}/service/${id}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, id]);

  // Update URL when language changes in context (e.g., from LanguageSwitcher)
  useEffect(() => {
    if (!id || !currentLanguage) return;
    
    // Only update if URL lang doesn't match currentLanguage and we have a valid lang in URL
    if (lang && lang !== currentLanguage && (lang === 'ar' || lang === 'en')) {
      navigate(`/${currentLanguage}/service/${id}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);
  
  // Fetch service - use activeLanguage from URL
  const { services: allServices } = useServices(true, activeLanguage);
  const [service, setService] = useState(null);
  const [subservices, setSubservices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Find the service by ID
  useEffect(() => {
    if (id && allServices && allServices.length > 0) {
      const found = allServices.find(s => 
        String(s.id) === String(id) || 
        String(s.Id) === String(id)
      );
      setService(found || null);
    } else {
      setService(null);
    }
  }, [id, allServices]);
  
  // Fetch subservices directly using the same method as TestServicesPage
  useEffect(() => {
    const fetchSubservices = async () => {
      if (!id || !service) {
        setSubservices([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log(`\n🔍 [ServiceDetailPage] Fetching subservices for service: ${service.title} (ID: ${service.id})`);
        const fetchedSubservices = await nocobaseService.getSubservices(service.id, false, activeLanguage);
        
        // Filter subservices to show only those with Status = "Approved"
        const approvedSubservices = (fetchedSubservices || []).filter(sub => {
          const status = sub.status || sub.Status || '';
          const isApproved = String(status).toLowerCase() === 'approved';
          if (!isApproved) {
            console.log(`⚠️ [ServiceDetailPage] Filtering out subservice "${sub.title}" - Status: ${status} (not Approved)`);
          }
          return isApproved;
        });
        
        setSubservices(approvedSubservices);
        console.log(`✅ [ServiceDetailPage] Found ${approvedSubservices.length} approved subservice(s) out of ${fetchedSubservices?.length || 0} total for service ${service.id}`);
        
        // Debug: Log all approved subservices with their linking fields
        if (approvedSubservices && approvedSubservices.length > 0) {
          console.log(`\n📋 [ServiceDetailPage] Approved subservices details:`, approvedSubservices.map(s => ({
            id: s.id,
            title: s.title,
            status: s.status || s.Status,
            Subservices: s.Subservices,
            parent_service_id: s.parent_service_id,
            main_service_id: s.main_service_id,
            service_id: s.service_id
          })));
        }
      } catch (error) {
        console.error(`❌ [ServiceDetailPage] Failed to fetch subservices for service ${service.id}:`, error);
        setSubservices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubservices();
  }, [id, service, activeLanguage]);
  
  // Debug: Log subservices
  useEffect(() => {
    if (service && subservices) {
      console.log('📋 [ServiceDetailPage] Service Details:', {
        serviceId: service.id,
        serviceTitle: service.title,
        subservicesCount: subservices.length,
        subservices: subservices.map(s => ({
          id: s.id,
          title: s.title,
          Subservices: s.Subservices,
          parent_service_id: s.parent_service_id,
          main_service_id: s.main_service_id,
          service_id: s.service_id
        }))
      });
    }
  }, [service, subservices]);
  
  // Fetch services for sidebar - use activeLanguage from URL
  const { services } = useServices(true, activeLanguage);

  // State to store details for main service
  const [serviceDetails, setServiceDetails] = useState(null);
  
  // State to store details for each subservice
  const [subservicesDetails, setSubservicesDetails] = useState({});
  
  // State to manage dialog open/close for each subservice
  const [openDialog, setOpenDialog] = useState(null);
  
  // State to manage selected subservice to display in main content
  const [selectedSubserviceId, setSelectedSubserviceId] = useState(null);
  
  // State for scroll position for parallax effect
  const [scrollY, setScrollY] = useState(0);
  const mainContentRef = useRef(null);
  
  // State to manage expanded subservices (accordion)
  const [expandedSubservices, setExpandedSubservices] = useState(new Set());
  
  // Reset selected subservice when service changes
  useEffect(() => {
    setSelectedSubserviceId(null);
  }, [id]);
  
  // Handle scroll for parallax effect - track scroll in main content area
  useEffect(() => {
    const handleScroll = (e) => {
      const scrollTop = e.target?.scrollTop || e.target?.scrollY || window.scrollY || 0;
      setScrollY(scrollTop);
    };
    
    const mainElement = mainContentRef.current;
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
      // Initial scroll position
      setScrollY(mainElement.scrollTop);
      return () => mainElement.removeEventListener('scroll', handleScroll);
    } else {
      // Fallback to window scroll
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  // Toggle dialog
  const toggleDialog = (subserviceId) => {
    setOpenDialog(prev => prev === subserviceId ? null : subserviceId);
  };
  
  // Close dialog
  const closeDialog = () => {
    setOpenDialog(null);
  };
  
  // Toggle expanded subservice (accordion)
  const toggleExpanded = (subserviceId) => {
    setExpandedSubservices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subserviceId)) {
        newSet.delete(subserviceId);
      } else {
        newSet.add(subserviceId);
      }
      return newSet;
    });
  };

  // Fetch details for main service
  useEffect(() => {
    const fetchServiceDetails = async () => {
      if (!service || !service.id) {
        setServiceDetails(null);
        return;
      }

      try {
        console.log(`\n═══════════════════════════════════════════════════════════`);
        console.log(`🔍 FETCHING DETAILS FOR MAIN SERVICE`);
        console.log(`═══════════════════════════════════════════════════════════`);
        console.log(`Service ID: ${service.id}`);
        console.log(`Service Title: ${service.title || service.name}`);
        console.log(`Service Object:`, JSON.stringify(service, null, 2));
        console.log(`═══════════════════════════════════════════════════════════\n`);
        
        const details = await nocobaseService.getServiceDetails(service.id, activeLanguage);
        if (details) {
          console.log(`✅ Found service details for service ${service.id}:`, {
            hasContent: !!details.content,
            hasFeatures: details.features?.length > 0,
            hasProcessSteps: details.process_steps?.length > 0,
            hasDocuments: details.documents_required?.length > 0,
            fullDetails: details
          });
          setServiceDetails(details);
        } else {
          console.warn(`⚠️ No details found for service ${service.id}`);
          setServiceDetails(null);
        }
      } catch (err) {
        console.error(`❌ Failed to fetch details for service ${service.id}:`, err);
        setServiceDetails(null);
      }
    };

    fetchServiceDetails();
  }, [service, activeLanguage]);

  // Fetch details for all subservices
  useEffect(() => {
    const fetchSubservicesDetails = async () => {
      if (!subservices || subservices.length === 0) {
        setSubservicesDetails({});
        return;
      }

      console.log(`\n🔍 [ServiceDetailPage] Fetching details for ${subservices.length} subservice(s)...`);
      const detailsMap = {};
      
      // Fetch details for each subservice
      for (const subservice of subservices) {
        try {
          console.log(`\n📋 Fetching details for subservice: "${subservice.title}" (ID: ${subservice.id})`);
          console.log(`   Subservice raw data:`, {
            id: subservice.id,
            title: subservice.title,
            description_en: subservice.description_en,
            description_ar: subservice.description_ar,
            content_en: subservice.content_en,
            content_ar: subservice.content_ar,
            process_steps_eng: subservice.process_steps_eng,
            process_steps_arb: subservice.process_steps_arb,
            documents_required_eng: subservice.documents_required_eng,
            documents_required_arb: subservice.documents_required_arb,
            duration: subservice.duration,
            allKeys: Object.keys(subservice)
          });
          
          const details = await nocobaseService.getServiceDetails(subservice.id, activeLanguage);
          if (details) {
            console.log(`✅ Found details for subservice ${subservice.id}:`, {
              hasContent: !!details.content,
              hasContentAr: !!details.content_ar,
              hasContentEn: !!details.content_en,
              hasFeatures: details.features?.length > 0,
              hasProcessSteps: details.process_steps?.length > 0,
              hasProcessStepsArb: !!details.process_steps_arb,
              hasProcessStepsEng: !!details.process_steps_eng,
              hasDocuments: details.documents_required?.length > 0,
              hasDocumentsArb: !!details.documents_required_arb,
              hasDocumentsEng: !!details.documents_required_eng,
              hasDuration: !!details.duration,
              fullDetails: details
            });
            detailsMap[subservice.id] = details;
          } else {
            console.warn(`⚠️ No details found in Service_Details for subservice ${subservice.id}, will use subservice fields directly`);
          }
        } catch (err) {
          console.error(`❌ Failed to fetch details for subservice ${subservice.id}:`, err);
        }
      }
      
      console.log(`\n✅ [ServiceDetailPage] Fetched details for ${Object.keys(detailsMap).length} out of ${subservices.length} subservice(s)`);
      setSubservicesDetails(detailsMap);
    };

    fetchSubservicesDetails();
  }, [subservices, activeLanguage]);

  // Memoize arrays to prevent reference changes
  const memoizedServices = useMemo(() => services || [], [services]);

  // Memoize description with HTML stripped
  const description = useMemo(() => {
    let desc = '';
    if (activeLanguage === 'ar') {
      desc = service?.description_ar || service?.description_en || '';
    } else {
      desc = service?.description_en || service?.description_ar || '';
    }
    return stripHtmlTags(desc);
  }, [service, activeLanguage]);

  const error = null; // No error state for now
  
  const skeletonShimmer = 'linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 50%, #e2e8f0 100%)';
  const skeletonStyle = (delay = 0) => ({
    background: skeletonShimmer,
    backgroundSize: '200% 100%',
    animation: 'skeleton-shimmer 1.5s ease-in-out infinite',
    animationDelay: `${delay}s`,
    borderRadius: '8px'
  });

  if (loading || !service) {
    return (
      <div style={{
        minHeight: contentOnly ? 'auto' : '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f9fafb'
      }} dir={activeIsRTL ? 'rtl' : 'ltr'}>
        {!contentOnly && <MemoizedHeader onOpenChat={onOpenChat} />}
        {!contentOnly && <div style={{ height: '100px', width: '100%' }} />}
        <div style={{
          display: 'flex',
          width: '100%',
          minHeight: contentOnly ? 'auto' : 'calc(100vh - 180px)'
        }}>
          {!contentOnly && (
            <aside style={{
              width: '20%',
              minWidth: '200px',
              flexShrink: 0,
              padding: '20px',
              borderRight: activeIsRTL ? 'none' : '1px solid #e2e8f0',
              borderLeft: activeIsRTL ? '1px solid #e2e8f0' : 'none'
            }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} style={{ height: '36px', marginBottom: '12px', ...skeletonStyle(i * 0.08) }} />
              ))}
            </aside>
          )}
          <main style={{
            flex: 1,
            minWidth: 0,
            padding: '40px',
            paddingLeft: activeIsRTL ? '40px' : '60px',
            paddingRight: activeIsRTL ? '60px' : '40px'
          }}>
            {/* Breadcrumbs */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <div style={{ height: '14px', width: '52px', ...skeletonStyle(0) }} />
              <div style={{ height: '10px', width: '8px', backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
              <div style={{ height: '14px', width: '64px', ...skeletonStyle(0.05) }} />
              <div style={{ height: '10px', width: '8px', backgroundColor: '#cbd5e1', borderRadius: '2px' }} />
              <div style={{ height: '14px', width: '40%', maxWidth: '200px', ...skeletonStyle(0.1) }} />
            </div>
            {/* Image placeholder */}
            <div style={{
              width: '100%',
              aspectRatio: '16/9',
              maxHeight: '400px',
              borderRadius: '12px',
              marginBottom: '32px',
              overflow: 'hidden',
              ...skeletonStyle(0.15)
            }} />
            {/* Title */}
            <div style={{ height: '40px', width: '65%', maxWidth: '480px', marginBottom: '16px', ...skeletonStyle(0.2) }} />
            {/* Description lines */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ height: '18px', width: '100%', marginBottom: '12px', ...skeletonStyle(0.25) }} />
              <div style={{ height: '18px', width: '92%', marginBottom: '12px', ...skeletonStyle(0.3) }} />
              <div style={{ height: '18px', width: '78%', ...skeletonStyle(0.35) }} />
            </div>
            {/* Content cards */}
            {[1, 2, 3].map((i) => (
              <div key={i} style={{
                marginBottom: '24px',
                padding: '24px',
                background: 'white',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
              }}>
                <div style={{ height: '22px', width: '180px', marginBottom: '16px', ...skeletonStyle(0.4 + i * 0.1) }} />
                <div style={{ height: '14px', width: '100%', marginBottom: '10px', ...skeletonStyle(0.45 + i * 0.1) }} />
                <div style={{ height: '14px', width: '95%', marginBottom: '10px', ...skeletonStyle(0.5 + i * 0.1) }} />
                <div style={{ height: '14px', width: '70%', ...skeletonStyle(0.55 + i * 0.1) }} />
              </div>
            ))}
          </main>
        </div>
        {!contentOnly && <MemoizedFooter />}
        <style>{`
          @keyframes skeleton-shimmer {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MemoizedHeader onOpenChat={onOpenChat} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Error Loading Service</h1>
            <p className="text-gray-600 mb-4">{error.message || 'Failed to load service details'}</p>
            <Link to="/" className="text-blue-600 hover:underline">Return to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MemoizedHeader onOpenChat={onOpenChat} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Service Not Found</h1>
            <p className="text-gray-600 mb-4">The requested service could not be found.</p>
            <Link to="/" className="text-blue-600 hover:underline">Return to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: contentOnly ? 'auto' : '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      backgroundColor: '#f9fafb'
    }} dir={activeIsRTL ? 'rtl' : 'ltr'}>
      {!contentOnly && <MemoizedHeader onOpenChat={onOpenChat} />}
      {!contentOnly && <div style={{ height: '100px', width: '100%' }} />}
      <div style={{
        display: 'flex',
        width: '100%',
        position: 'relative',
        minHeight: contentOnly ? 'auto' : 'calc(100vh - 180px)',
        marginBottom: '0'
      }}>
        {!contentOnly && (
        <aside className="hidden lg:block" style={{
          width: '20%',
          flexShrink: 0,
          position: 'sticky',
          top: '100px',
          alignSelf: 'flex-start',
          height: 'calc(100vh - 100px)',
          maxHeight: 'calc(100vh - 100px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px',
          paddingLeft: '0',
          backgroundColor: '#f9fafb',
          borderRight: '1px solid #e2e8f0'
        }}>
          <MemoizedSidebar
            services={memoizedServices}
            currentService={service}
            currentSubservice={selectedSubserviceId ? subservices.find(s => String(s.id) === String(selectedSubserviceId)) : null}
            subservices={subservices || []}
            language={activeLanguage}
            onServiceClick={(serviceId) => {
              setSelectedSubserviceId(null);
              navigate(`/${activeLanguage}/service/${serviceId}`, { replace: false });
            }}
            onSubserviceClick={(subserviceId) => {
              // Open the subservice in accordion and scroll to subservices section
              setSelectedSubserviceId(null); // Don't show in main content
              setExpandedSubservices(new Set([subserviceId])); // Open only this subservice
              // Scroll to subservices section
              setTimeout(() => {
                const subservicesSection = document.getElementById('subservices-section');
                if (subservicesSection) {
                  subservicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            onOpenChat={onOpenChat}
          />
        </aside>
        )}

        {/* Column 2: Main Content and Subservices */}
        <main 
          ref={mainContentRef}
          style={{
            flex: 1,
            minWidth: 0,
            maxWidth: '100%',
            padding: '40px',
            paddingLeft: '60px',
            paddingBottom: '0',
            overflowY: 'auto',
            position: 'relative'
          }}
        >
          {/* Breadcrumbs */}
          <nav className="service-breadcrumb" style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <Link 
              to="/" 
              style={{
                color: '#64748b',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#1a1a2e'}
              onMouseLeave={(e) => e.target.style.color = '#64748b'}
            >
              {activeLanguage === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <span className="service-breadcrumb-sep" style={{ color: '#94a3b8' }} aria-hidden="true">/</span>
            <Link 
              to="/#services" 
              style={{
                color: '#64748b',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.color = '#1a1a2e'}
              onMouseLeave={(e) => e.target.style.color = '#64748b'}
            >
              {activeLanguage === 'ar' ? 'الخدمات' : 'Services'}
            </Link>
            <span className="service-breadcrumb-sep" style={{ color: '#94a3b8' }} aria-hidden="true">/</span>
            <span className="service-breadcrumb-current" style={{ 
              color: '#1a1a2e', 
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {activeLanguage === 'ar' 
                ? (service?.title_ar || service?.title || service?.name || 'Service Details')
                : (service?.title || service?.title_en || service?.name || 'Service Details')}
            </span>
          </nav>

          {/* Service Image with Title Overlay - Hide on Scroll, Keep Title Fixed */}
          {service?.image && (
            <>
              {/* Image Container - Hides on scroll */}
              <div style={{
                width: '100%',
                aspectRatio: '16/9',
                maxHeight: '600px',
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '40px',
                position: 'relative',
                display: 'flex',
                alignItems: 'flex-end',
                opacity: Math.max(0, 1 - scrollY * 0.004),
                transform: `translateY(${Math.min(0, -scrollY * 0.3)}px) scale(${Math.max(0.95, 1 - scrollY * 0.001)})`,
                transition: 'opacity 0.3s ease-out, transform 0.3s ease-out',
                pointerEvents: scrollY > 250 ? 'none' : 'auto',
                backgroundColor: '#f1f5f9'
              }}>
                <img
                  src={service.image}
                  alt={service.title || service.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center center',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Dark Overlay Gradient for Text Readability */}
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.4) 50%, rgba(0, 0, 0, 0) 100%)',
                  pointerEvents: 'none'
                }}></div>
                
                {/* Service Title - Inside Image in Default State */}
                {!selectedSubserviceId && scrollY <= 250 && (
                  <div style={{
                    position: 'relative',
                    zIndex: 2,
                    width: '100%',
                    padding: '40px',
                    paddingBottom: '50px',
                    opacity: Math.max(0, 1 - scrollY * 0.005),
                    transform: `translateY(${Math.min(0, scrollY * 0.2)}px)`,
                    transition: 'opacity 0.3s ease-out, transform 0.3s ease-out'
                  }}>
                    <h1 style={{
                      fontSize: 'clamp(36px, 5vw, 56px)',
                      fontWeight: '700',
                      lineHeight: '1.2',
                      margin: 0,
                      color: '#ffffff',
                      letterSpacing: '-0.02em',
                      textAlign: activeIsRTL ? 'right' : 'left',
                      textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                    }}>
                      {activeLanguage === 'ar' 
                        ? (service?.title_ar || service?.title || service?.name || 'Service Details')
                        : (service?.title || service?.title_en || service?.name || 'Service Details')}
                    </h1>
                  </div>
                )}
              </div>
              
              {/* Service Title - Appears when Image is Hidden (No Sticky) */}
              {!selectedSubserviceId && scrollY > 150 && (
                <div style={{
                  width: '100%',
                  padding: '12px 0',
                  marginTop: scrollY > 250 ? '-30px' : `${-30 + (250 - scrollY) * 0.3}px`,
                  marginBottom: '30px',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease-out',
                  position: 'relative',
                  zIndex: 10,
                  opacity: Math.min(1, Math.max(0, (scrollY - 150) * 0.01))
                }}>
                  <h1 style={{
                    fontSize: 'clamp(28px, 4vw, 40px)',
                    fontWeight: '700',
                    lineHeight: '1.2',
                    margin: 0,
                    padding: '0 20px',
                    color: '#1a1a2e',
                    letterSpacing: '-0.02em',
                    textAlign: activeIsRTL ? 'right' : 'left',
                    transition: 'all 0.3s ease-out'
                  }}>
                    {activeLanguage === 'ar' 
                      ? (service?.title_ar || service?.title || service?.name || 'Service Details')
                      : (service?.title || service?.title_en || service?.name || 'Service Details')}
                  </h1>
                </div>
              )}
            </>
          )}

          {/* Service Title or Selected Subservice */}
          {selectedSubserviceId ? (
            (() => {
              const selectedSubservice = subservices.find(s => String(s.id) === String(selectedSubserviceId));
              const selectedSubserviceDetails = subservicesDetails[selectedSubserviceId];
              
              if (!selectedSubservice) {
                return (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    {activeLanguage === 'ar' ? 'الخدمة الفرعية غير موجودة' : 'Subservice not found'}
                  </div>
                );
              }
              
              return (
                <div>
                  <button
                    onClick={() => setSelectedSubserviceId(null)}
                    style={{
                      padding: '8px 16px',
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      color: '#475569',
                      fontSize: '14px',
                      marginBottom: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {activeLanguage === 'ar' ? 'العودة إلى الخدمة الرئيسية' : 'Back to Main Service'}
                  </button>
                  <h1 style={{
                    fontSize: 'clamp(32px, 5vw, 48px)',
                    fontWeight: '700',
                    lineHeight: '1.2',
                    marginBottom: '30px',
                    color: '#1a1a2e',
                    letterSpacing: '-0.02em',
                    textAlign: activeIsRTL ? 'right' : 'left'
                  }}>
                    {activeLanguage === 'ar' 
                      ? (selectedSubservice?.title_ar || selectedSubservice?.title || selectedSubservice?.name || 'Subservice Details')
                      : (selectedSubservice?.title || selectedSubservice?.title_en || selectedSubservice?.name || 'Subservice Details')}
                  </h1>
                  <ServiceDetailContent
                    subservice={selectedSubservice}
                    details={selectedSubserviceDetails}
                    language={activeLanguage}
                    onOpenChat={onOpenChat}
                  />
                </div>
              );
            })()
          ) : (
            <>
              {/* Service Title - Only show if no image */}
              {!service?.image && (
                <h1 style={{
                  fontSize: 'clamp(32px, 5vw, 48px)',
                  fontWeight: '700',
                  lineHeight: '1.2',
                  marginBottom: '30px',
                  color: '#1a1a2e',
                  letterSpacing: '-0.02em',
                  textAlign: activeIsRTL ? 'right' : 'left'
                }}>
                  {activeLanguage === 'ar' 
                    ? (service?.title_ar || service?.title || service?.name || 'Service Details')
                    : (service?.title || service?.title_en || service?.name || 'Service Details')}
                </h1>
              )}
              {/* Service Content */}
              <ServiceDetailContent
                subservice={service}
                details={serviceDetails}
                language={activeLanguage}
                onOpenChat={onOpenChat}
              />
            </>
          )}

          {/* Subservices Section - Always show when subservices exist */}
          {subservices && subservices.length > 0 ? (
            <div id="subservices-section" style={{ marginTop: '40px' }}>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '700',
                color: '#1a1a2e',
                marginBottom: '30px',
                textAlign: activeIsRTL ? 'right' : 'left'
              }}>
                {activeLanguage === 'ar' ? 'الخدمات' : 'Services'}
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {subservices.map((subservice, index) => {
                  const details = subservicesDetails[subservice.id];
                  const isDialogOpen = openDialog === subservice.id;
                  
                  // Get ALL fields from subservice and details
                  // Content - prioritize details, then subservice
                  let content = details 
                    ? (activeLanguage === 'ar' 
                        ? (details.content_ar || details.content_en || '')
                        : (details.content_en || details.content_ar || ''))
                    : (activeLanguage === 'ar'
                        ? (subservice.content_ar || subservice.content_en || '')
                        : (subservice.content_en || subservice.content_ar || ''));
                  
                  // Debug: Log content to see its format
                  if (content) {
                    console.log(`\n📄 [ServiceDetailPage] Content for subservice "${subservice.title}" (ID: ${subservice.id}):`, {
                      contentType: typeof content,
                      contentLength: content.length,
                      contentPreview: content.substring(0, 200),
                      isHTML: content.includes('<') && content.includes('>'),
                      hasHTMLTags: /<[^>]+>/.test(content),
                      rawContent: content
                    });
                  }
                  
                  // Ensure content is a string and handle rich text format
                  if (content && typeof content !== 'string') {
                    try {
                      content = JSON.stringify(content);
                    } catch (e) {
                      content = String(content);
                    }
                  }
                  
                  // If content is a JSON string, try to parse it
                  if (content && typeof content === 'string' && content.trim().startsWith('{')) {
                    try {
                      const parsed = JSON.parse(content);
                      // If it's a rich text object, extract the HTML
                      if (parsed && typeof parsed === 'object') {
                        content = parsed.html || parsed.content || parsed.text || JSON.stringify(parsed);
                      }
                    } catch (e) {
                      // Not JSON, keep as is
                    }
                  }
                  
                  // Description - prioritize details, then subservice, then content
                  let descriptionRaw = '';
                  if (details) {
                    if (activeLanguage === 'ar') {
                      descriptionRaw = details.description_ar || details.description_en || details.content_ar || details.content_en || '';
                    } else {
                      descriptionRaw = details.description_en || details.description_ar || details.content_en || details.content_ar || '';
                    }
                  }
                  // If no details, try from subservice
                  if (!descriptionRaw) {
                    if (activeLanguage === 'ar') {
                      descriptionRaw = subservice.description_ar || subservice.description_en || subservice.content_ar || subservice.content_en || '';
                    } else {
                      descriptionRaw = subservice.description_en || subservice.description_ar || subservice.content_en || subservice.content_ar || '';
                    }
                  }
                  const description = descriptionRaw ? stripHtmlTags(descriptionRaw) : '';
                  
                  // Process steps - prioritize details, then subservice
                  let processSteps = [];
                  if (details) {
                    if (activeLanguage === 'ar') {
                      processSteps = details.process_steps_arb || details.process_steps_ar || details.process_steps || [];
                    } else {
                      processSteps = details.process_steps_eng || details.process_steps_en || details.process_steps || [];
                    }
                  }
                  // If no details, try from subservice directly
                  if (processSteps.length === 0) {
                    if (activeLanguage === 'ar') {
                      const stepsAr = subservice.process_steps_arb || subservice.process_steps_ar;
                      if (stepsAr) {
                        try {
                          if (typeof stepsAr === 'string') {
                            if (stepsAr.trim().startsWith('[') || stepsAr.trim().startsWith('{')) {
                              processSteps = JSON.parse(stepsAr);
                            } else {
                              processSteps = stepsAr.split(/\n|•|·|-/).map(p => p.trim()).filter(p => p.length > 0);
                            }
                          } else if (Array.isArray(stepsAr)) {
                            processSteps = stepsAr;
                          }
                        } catch (e) {
                          processSteps = [];
                        }
                      }
                    } else {
                      const stepsEn = subservice.process_steps_eng || subservice.process_steps_en;
                      if (stepsEn) {
                        try {
                          if (typeof stepsEn === 'string') {
                            if (stepsEn.trim().startsWith('[') || stepsEn.trim().startsWith('{')) {
                              processSteps = JSON.parse(stepsEn);
                            } else {
                              processSteps = stepsEn.split(/\n|•|·|-/).map(p => p.trim()).filter(p => p.length > 0);
                            }
                          } else if (Array.isArray(stepsEn)) {
                            processSteps = stepsEn;
                          }
                        } catch (e) {
                          processSteps = [];
                        }
                      }
                    }
                  }
                  
                  // Documents required - prioritize details, then subservice
                  let documentsRequired = [];
                  if (details) {
                    if (activeLanguage === 'ar') {
                      documentsRequired = details.documents_required_arb || details.documents_required_ar || details.documents_required || [];
                    } else {
                      documentsRequired = details.documents_required_eng || details.documents_required_en || details.documents_required || [];
                    }
                  }
                  // If no details, try from subservice directly
                  if (documentsRequired.length === 0) {
                    if (activeLanguage === 'ar') {
                      const docsAr = subservice.documents_required_arb || subservice.documents_required_ar;
                      if (docsAr) {
                        try {
                          if (typeof docsAr === 'string') {
                            if (docsAr.trim().startsWith('[') || docsAr.trim().startsWith('{')) {
                              documentsRequired = JSON.parse(docsAr);
                            } else {
                              documentsRequired = docsAr.split(/\n|•|·|,|;/).map(d => d.trim()).filter(d => d.length > 0);
                            }
                          } else if (Array.isArray(docsAr)) {
                            documentsRequired = docsAr;
                          }
                        } catch (e) {
                          documentsRequired = [];
                        }
                      }
                    } else {
                      const docsEn = subservice.documents_required_eng || subservice.documents_required_en;
                      if (docsEn) {
                        try {
                          if (typeof docsEn === 'string') {
                            if (docsEn.trim().startsWith('[') || docsEn.trim().startsWith('{')) {
                              documentsRequired = JSON.parse(docsEn);
                            } else {
                              documentsRequired = docsEn.split(/\n|•|·|,|;/).map(d => d.trim()).filter(d => d.length > 0);
                            }
                          } else if (Array.isArray(docsEn)) {
                            documentsRequired = docsEn;
                          }
                        } catch (e) {
                          documentsRequired = [];
                        }
                      }
                    }
                  }
                  
                  // Duration - from details or subservice
                  const duration = details?.duration || subservice.duration || '';
                  
                  // Features - from details only (not in subservice table)
                  const features = details?.features || [];
                  
                  // Status - from subservice (not displayed, only used for filtering)
                  const status = subservice.status || subservice.Status || '';
                  
                  // Order - from subservice
                  const order = subservice.order || 0;
                  
                  // Active - from subservice
                  const active = subservice.active !== undefined ? subservice.active : true;
                  
                  // Category - from subservice (relation field)
                  const category = subservice.category || [];
                  
                  // Insights - from subservice
                  const insights = subservice.Insights || subservice.insights || '';
                  
                  // Check if there's additional content to show in dialog
                  const hasAdditionalContent = !!(
                    content ||
                    processSteps.length > 0 ||
                    documentsRequired.length > 0 ||
                    duration ||
                    features.length > 0 ||
                    category.length > 0 ||
                    insights ||
                    descriptionRaw // Show button if there's at least description
                  );
                  
                  // Debug: Log subservice data
                  console.log(`\n📋 [ServiceDetailPage] Rendering subservice "${subservice.title}" (ID: ${subservice.id}):`, {
                    hasDetails: !!details,
                    hasContent: !!content,
                    contentLength: content?.length || 0,
                    hasDescription: !!description,
                    descriptionLength: description?.length || 0,
                    hasProcessSteps: processSteps.length > 0,
                    processStepsCount: processSteps.length,
                    hasDocuments: documentsRequired.length > 0,
                    documentsCount: documentsRequired.length,
                    hasDuration: !!duration,
                    duration: duration,
                    hasFeatures: features.length > 0,
                    featuresCount: features.length,
                    hasCategory: category.length > 0,
                    categoryCount: category.length,
                    hasInsights: !!insights,
                    insights: insights,
                    status: status,
                    subserviceRaw: {
                      content_en: subservice.content_en,
                      content_ar: subservice.content_ar,
                      process_steps_eng: subservice.process_steps_eng,
                      process_steps_arb: subservice.process_steps_arb,
                      documents_required_eng: subservice.documents_required_eng,
                      documents_required_arb: subservice.documents_required_arb,
                      duration: subservice.duration
                    }
                  });

                  const isExpanded = expandedSubservices.has(subservice.id);
                  
                  return (
                    <React.Fragment key={subservice.id}>
                      {/* Subservice Card - Collapsible */}
                      <div
                        style={{
                          background: 'white',
                          borderRadius: '12px',
                          padding: '0',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.3s ease',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)';
                        }}
                      >
                        {/* Title - Clickable Header */}
                        <div
                          onClick={() => toggleExpanded(subservice.id)}
                          style={{
                            padding: '24px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '16px',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <h3 style={{
                            fontSize: '22px',
                            fontWeight: '700',
                            color: '#1a1a2e',
                            margin: 0,
                            lineHeight: '1.3',
                            textAlign: activeIsRTL ? 'right' : 'left',
                            flex: 1
                          }}>
                            {activeLanguage === 'ar' 
                              ? (subservice.title_ar || subservice.title || subservice.name)
                              : (subservice.title || subservice.title_en || subservice.name)}
                          </h3>
                          
                          {/* Expand/Collapse Icon */}
                          <svg
                            width="24"
                            height="24"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            style={{
                              color: '#1a1a2e',
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease',
                              flexShrink: 0
                            }}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                        
                        {/* Description - Collapsible Content */}
                        {isExpanded && (
                          <div style={{
                            padding: '24px',
                            borderTop: '1px solid #e2e8f0',
                            animation: 'slideDown 0.3s ease-out'
                          }}>
                            {descriptionRaw && descriptionRaw.trim() ? (
                              <div 
                                className="rich-text-content"
                                style={{
                                  fontSize: '16px',
                                  lineHeight: '1.7',
                                  color: '#64748b',
                                  marginTop: 0,
                                  marginRight: 0,
                                  marginBottom: 0,
                                  marginLeft: 0,
                                  textAlign: activeIsRTL ? 'right' : 'left'
                                }}
                                dangerouslySetInnerHTML={{ __html: sanitizeRichText(descriptionRaw) }}
                              />
                            ) : (
                              <p style={{
                                fontSize: '16px',
                                lineHeight: '1.7',
                                color: '#94a3b8',
                                fontStyle: 'italic',
                                marginTop: 0,
                                marginRight: 0,
                                marginBottom: 0,
                                marginLeft: 0,
                                textAlign: activeIsRTL ? 'right' : 'left'
                              }}>
                                {activeLanguage === 'ar' ? 'لا يوجد وصف متاح' : 'No description available'}
                              </p>
                            )}
                            
                            {/* Learn More Button - Show only if there's additional content */}
                            {hasAdditionalContent && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleDialog(subservice.id);
                                }}
                                style={{
                                  padding: '12px 24px',
                                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  fontSize: '15px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '0 2px 8px rgba(26, 26, 46, 0.2)',
                                  marginTop: '20px',
                                  textAlign: activeIsRTL ? 'right' : 'left'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.transform = 'translateY(-2px)';
                                  e.target.style.boxShadow = '0 4px 12px rgba(26, 26, 46, 0.3)';
                                  e.target.style.background = 'linear-gradient(135deg, #16213e 0%, #0f3460 100%)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = '0 2px 8px rgba(26, 26, 46, 0.2)';
                                  e.target.style.background = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)';
                                }}
                              >
                                {activeLanguage === 'ar' ? 'تعرف أكثر على' : 'Learn More'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Dialog/Modal for Full Details */}
                      {isDialogOpen && (
                        <div
                          style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0, 0, 0, 0.6)',
                            zIndex: 10000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '20px',
                            animation: 'fadeIn 0.3s ease'
                          }}
                          onClick={closeDialog}
                        >
                          <div
                            style={{
                              background: 'white',
                              borderRadius: '16px',
                              maxWidth: '900px',
                              width: '100%',
                              maxHeight: '90vh',
                              overflow: 'auto',
                              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                              position: 'relative',
                              animation: 'slideUp 0.3s ease',
                              direction: activeIsRTL ? 'rtl' : 'ltr'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Dialog Header */}
                            <div style={{
                              padding: '24px',
                              borderBottom: '1px solid #e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              position: 'sticky',
                              top: 0,
                              background: 'white',
                              zIndex: 1
                            }}>
                              <h2 style={{
                                fontSize: '24px',
                                fontWeight: '700',
                                color: '#1a1a2e',
                                margin: 0,
                                textAlign: activeIsRTL ? 'right' : 'left'
                              }}>
                                {activeLanguage === 'ar' 
                                  ? (subservice.title_ar || subservice.title || subservice.name)
                                  : (subservice.title || subservice.title_en || subservice.name)}
                              </h2>
                              <button
                                onClick={closeDialog}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '8px',
                                  borderRadius: '8px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                            
                            {/* Dialog Content */}
                            <div style={{ padding: '24px' }}>
                              {/* Subservice Image */}
                              {subservice.image && (
                                <div style={{
                                  width: '100%',
                                  height: '250px',
                                  borderRadius: '10px',
                                  marginBottom: '24px',
                                  overflow: 'hidden',
                                  background: '#f1f5f9'
                                }}>
                                  <img
                                    src={subservice.image}
                                    alt={subservice.title || subservice.name}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                        
                              {/* Content Section - Show if content exists */}
                              {content && content.trim() && (
                                <section style={{
                                  marginBottom: '24px'
                                }}>
                                  <h4 style={{
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1a1a2e',
                                    marginBottom: '12px',
                                    textAlign: activeIsRTL ? 'right' : 'left'
                                  }}>
                                    {activeLanguage === 'ar' ? 'عن الخدمة' : 'About the Service'}
                                  </h4>
                                  <div 
                                    className="rich-text-content"
                                    style={{
                                      fontSize: '16px',
                                      lineHeight: '1.8',
                                      color: '#374151',
                                      fontFamily: 'system-ui, -apple-system, sans-serif',
                                      textAlign: activeIsRTL ? 'right' : 'left'
                                    }}
                                    dangerouslySetInnerHTML={{ __html: sanitizeRichText(content) }}
                                  />
                                </section>
                              )}

                              {/* Features Section */}
                              {features.length > 0 && (
                                <section style={{
                                  marginBottom: '24px'
                                }}>
                                  <h4 style={{
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1a1a2e',
                                    marginBottom: '14px',
                                    textAlign: activeIsRTL ? 'right' : 'left'
                                  }}>
                                    {activeLanguage === 'ar' ? 'المميزات الرئيسية' : 'Key Features'}
                                  </h4>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '16px'
                          }}>
                            {features.map((feature, idx) => (
                              <div
                                key={idx}
                                style={{
                                  padding: '16px',
                                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                                  borderRadius: '10px',
                                  border: '1px solid #e2e8f0'
                                }}
                              >
                                <p style={{
                                  fontSize: '16px',
                                  lineHeight: '1.6',
                                  color: '#1e293b',
                                  margin: 0,
                                  fontWeight: '500',
                                  textAlign: activeIsRTL ? 'right' : 'left'
                                }}>
                                  {typeof feature === 'string' ? feature : feature.title || feature.name || JSON.stringify(feature)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </section>
                      )}

                              {/* Process Steps Section */}
                              {processSteps.length > 0 && (
                                <section style={{
                                  marginBottom: '24px'
                                }}>
                                  <h4 style={{
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1a1a2e',
                                    marginBottom: '16px',
                                    textAlign: activeIsRTL ? 'right' : 'left'
                                  }}>
                                    {activeLanguage === 'ar' ? 'خطوات العملية' : 'Process Steps'}
                                  </h4>
                          <div style={{ position: 'relative', paddingLeft: activeIsRTL ? '0' : '30px', paddingRight: activeIsRTL ? '30px' : '0' }}>
                            <div style={{
                              position: 'absolute',
                              [activeIsRTL ? 'right' : 'left']: '20px',
                              top: '0',
                              bottom: '0',
                              width: '2px',
                              background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)',
                              borderRadius: '2px'
                            }}></div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative' }}>
                              {processSteps.map((step, stepIndex) => {
                                const stepData = typeof step === 'string' 
                                  ? { description: step }
                                  : step;
                                const stepTitle = stepData.title || stepData.name || '';
                                const stepDesc = stepData.description || stepData.desc || stepData || '';

                                return (
                                  <div key={stepIndex} style={{ display: 'flex', gap: '16px', position: 'relative' }}>
                                    <div style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '50%',
                                      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '18px',
                                      fontWeight: '700',
                                      flexShrink: 0,
                                      boxShadow: '0 4px 12px rgba(26, 26, 46, 0.3)',
                                      zIndex: 1
                                    }}>
                                      {stepIndex + 1}
                                    </div>
                                    <div style={{ flex: 1, paddingTop: '2px' }}>
                                      {stepTitle && (
                                        <h5 style={{
                                          fontSize: '18px',
                                          fontWeight: '600',
                                          color: '#1a1a2e',
                                          marginBottom: '6px',
                                          textAlign: activeIsRTL ? 'right' : 'left'
                                        }}>
                                          {stepTitle}
                                        </h5>
                                      )}
                                      <p style={{
                                        fontSize: '15px',
                                        lineHeight: '1.6',
                                        color: '#64748b',
                                        margin: 0,
                                        textAlign: activeIsRTL ? 'right' : 'left'
                                      }}>
                                        {stepDesc}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </section>
                      )}

                              {/* Documents Required Section */}
                              {documentsRequired.length > 0 && (
                                <section style={{
                                  marginBottom: '24px'
                                }}>
                                  <h4 style={{
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1a1a2e',
                                    marginBottom: '14px',
                                    textAlign: activeIsRTL ? 'right' : 'left'
                                  }}>
                                    {activeLanguage === 'ar' ? 'المستندات المطلوبة' : 'Required Documents'}
                                  </h4>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '16px'
                          }}>
                            {documentsRequired.map((doc, docIndex) => (
                              <div
                                key={docIndex}
                                style={{
                                  padding: '12px 16px',
                                  background: '#f8fafc',
                                  borderRadius: '8px',
                                  border: '1px solid #e2e8f0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px'
                                }}
                              >
                                <div style={{
                                  width: '36px',
                                  height: '36px',
                                  borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  <svg
                                    width="20"
                                    height="20"
                                    fill="none"
                                    stroke="white"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                    />
                                  </svg>
                                </div>
                                <span style={{
                                  fontSize: '15px',
                                  color: '#1e293b',
                                  fontWeight: '500',
                                  textAlign: activeIsRTL ? 'right' : 'left',
                                  flex: 1
                                }}>
                                  {typeof doc === 'string' ? doc : doc.name || doc.title || doc.description || JSON.stringify(doc)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </section>
                      )}

                              {/* Duration Section */}
                              {duration && (
                                <section style={{
                                  marginBottom: '24px'
                                }}>
                                  <h4 style={{
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1a1a2e',
                                    marginBottom: '12px',
                                    textAlign: activeIsRTL ? 'right' : 'left'
                                  }}>
                                    {activeLanguage === 'ar' ? 'المدة المتوقعة' : 'Estimated Duration'}
                                  </h4>
                          <p style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            color: '#1a1a2e',
                            margin: 0,
                            textAlign: activeIsRTL ? 'right' : 'left'
                          }}>
                            {duration}
                          </p>
                        </section>
                      )}

                      {/* Status Section - REMOVED: Only show Approved subservices */}

                              {/* Category Section */}
                              {category && category.length > 0 && (
                                <section style={{
                                  marginBottom: '24px'
                                }}>
                                  <h4 style={{
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1a1a2e',
                                    marginBottom: '14px',
                                    textAlign: activeIsRTL ? 'right' : 'left'
                                  }}>
                                    {activeLanguage === 'ar' ? 'الفئة' : 'Category'}
                                  </h4>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '12px'
                          }}>
                            {category.map((cat, catIndex) => (
                              <span
                                key={catIndex}
                                style={{
                                  padding: '8px 16px',
                                  background: '#f1f5f9',
                                  borderRadius: '8px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: '#1e293b',
                                  border: '1px solid #e2e8f0'
                                }}
                              >
                                {typeof cat === 'string' ? cat : (cat.name || cat.title || JSON.stringify(cat))}
                              </span>
                            ))}
                          </div>
                        </section>
                      )}

                              {/* Insights Section */}
                              {insights && (
                                <section style={{
                                  marginBottom: '24px'
                                }}>
                                  <h4 style={{
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1a1a2e',
                                    marginBottom: '12px',
                                    textAlign: activeIsRTL ? 'right' : 'left'
                                  }}>
                                    {activeLanguage === 'ar' ? 'الرؤى' : 'Insights'}
                                  </h4>
                          <p style={{
                            fontSize: '16px',
                            lineHeight: '1.7',
                            color: '#374151',
                            margin: 0,
                            textAlign: activeIsRTL ? 'right' : 'left'
                          }}>
                            {insights}
                          </p>
                        </section>
                      )}

                              {/* Request Service Button */}
                              <div style={{
                                marginTop: '32px',
                                paddingTop: '24px',
                                textAlign: 'center'
                              }}>
                                <button
                                  onClick={() => {
                                    closeDialog();
                                    if (onOpenChat) {
                                      onOpenChat();
                                    }
                                  }}
                                  style={{
                                    padding: '14px 36px',
                                    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 4px 12px rgba(26, 26, 46, 0.3)',
                                    whiteSpace: 'nowrap',
                                    minWidth: '200px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(26, 26, 46, 0.4)';
                                    e.target.style.background = 'linear-gradient(135deg, #16213e 0%, #0f3460 100%)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 12px rgba(26, 26, 46, 0.3)';
                                    e.target.style.background = 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)';
                                  }}
                                >
                                  {activeLanguage === 'ar' ? 'طلب الخدمة' : 'Request Service'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              
            </div>
          ) : null}
        </main>

      </div>

      {!contentOnly && (
      <>
      {/* Footer - Full width at bottom */}
      <div style={{
        width: '100%',
        marginTop: '60px',
        paddingTop: '0'
      }}>
        <MemoizedFooter />
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden block" style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px'
      }}>
        {/* Explore Services - Mobile */}
        <div style={{ 
          width: '100%', 
          marginBottom: '24px'
        }}>
          <MemoizedSidebar
            services={memoizedServices}
            currentService={service}
            currentSubservice={selectedSubserviceId ? subservices.find(s => String(s.id) === String(selectedSubserviceId)) : null}
            subservices={subservices || []}
            language={activeLanguage}
            onServiceClick={(serviceId) => {
              setSelectedSubserviceId(null);
              navigate(`/${activeLanguage}/service/${serviceId}`, { replace: false });
            }}
            onSubserviceClick={(subserviceId) => {
              // Open the subservice in accordion and scroll to subservices section
              setSelectedSubserviceId(null); // Don't show in main content
              setExpandedSubservices(new Set([subserviceId])); // Open only this subservice
              // Scroll to subservices section
              setTimeout(() => {
                const subservicesSection = document.getElementById('subservices-section');
                if (subservicesSection) {
                  subservicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 100);
            }}
            onOpenChat={onOpenChat}
          />
        </div>

        {/* Footer - Mobile */}
        <div style={{
          marginTop: '60px',
          paddingTop: '40px'
        }}>
          <MemoizedFooter />
        </div>
      </div>
      </>
      )}

      {/* Responsive Styles */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            max-height: 1000px;
            transform: translateY(0);
          }
        }
        
        /* محاذاة مسار التنقل: النص والفواصل في مستوى واحد */
        .service-breadcrumb { align-items: center !important; }
        .service-breadcrumb > a,
        .service-breadcrumb > span {
          display: inline-flex !important;
          align-items: center !important;
          line-height: 1.2 !important;
        }
        .service-breadcrumb-sep {
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          line-height: 1 !important;
          font-size: inherit !important;
        }
        
        @media (max-width: 1024px) {
          /* Hide desktop sidebar on mobile/tablet */
          aside.hidden.lg\\:block {
            display: none !important;
          }
          
          /* Show mobile sections */
          div.lg\\:hidden.block {
            display: block !important;
          }
          
          /* Full width for main content on mobile */
          main {
            width: 100% !important;
            max-width: 100% !important;
            marginLeft: 0 !important;
            position: relative !important;
            top: auto !important;
            bottom: auto !important;
            height: auto !important;
            padding: 20px !important;
            paddingLeft: 20px !important;
          }
          
          /* Adjust footer for mobile */
          div[style*="position: 'fixed'"][style*="bottom: '0'"] {
            position: relative !important;
          }
        }
        
        @media (min-width: 1025px) {
          /* Hide mobile sections on desktop */
          div.lg\\:hidden.block {
            display: none !important;
          }
        }
        
        /* ========== جوال فقط: صفحة الخدمات ========== */
        @media (max-width: 768px) {
          main {
            padding: 16px 14px 24px !important;
            padding-left: 14px !important;
            padding-right: 14px !important;
          }
          .service-breadcrumb {
            display: flex !important;
            flex-wrap: wrap !important;
            align-items: center !important;
            gap: 4px 6px !important;
            margin-bottom: 14px !important;
            line-height: 1.4 !important;
          }
          .service-breadcrumb a,
          .service-breadcrumb span:not(.service-breadcrumb-current) {
            font-size: 12px !important;
          }
          .service-breadcrumb-sep { font-size: 12px !important; }
          .service-breadcrumb-current {
            font-size: 12px !important;
            max-width: 100% !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }
          main nav {
            gap: 6px !important;
            margin-bottom: 16px !important;
            flex-wrap: wrap !important;
          }
          main nav a, main nav span {
            font-size: 13px !important;
          }
          main h1 {
            font-size: clamp(22px, 5.5vw, 28px) !important;
            line-height: 1.25 !important;
          }
          /* صورة الخدمة على الجوال */
          main > div[style*="aspectRatio"] {
            margin-bottom: 24px !important;
            border-radius: 10px !important;
            max-height: 280px !important;
          }
          main > div[style*="aspectRatio"] > div:last-of-type {
            padding: 24px 16px 28px !important;
          }
          main > div[style*="aspectRatio"] h1 {
            font-size: clamp(20px, 5vw, 26px) !important;
          }
          /* عنوان بدون صورة */
          main > h1 {
            margin-bottom: 20px !important;
          }
          .rich-text-content {
            font-size: 15px !important;
            line-height: 1.7 !important;
          }
          #subservices-section {
            margin-top: 28px !important;
          }
          #subservices-section h2 {
            font-size: 22px !important;
            margin-bottom: 20px !important;
          }
          #subservices-section > div {
            gap: 16px !important;
          }
          /* بطاقات الخدمات الفرعية (أكورديون) */
          #subservices-section > div > div {
            border-radius: 12px !important;
          }
          #subservices-section > div > div > div:first-child {
            padding: 18px 16px !important;
          }
          #subservices-section > div > div > div:first-child h3 {
            font-size: 17px !important;
            line-height: 1.35 !important;
          }
          #subservices-section > div > div > div:not(:first-child) {
            padding: 16px !important;
          }
          #subservices-section .rich-text-content {
            font-size: 14px !important;
          }
          #subservices-section button {
            padding: 12px 20px !important;
            font-size: 14px !important;
            min-height: 44px !important;
          }
          /* نافذة تفاصيل الخدمة الفرعية (مودال) */
          [style*="maxWidth: '900px'"][style*="maxHeight: '90vh'"] {
            max-height: 85vh !important;
            margin: 12px !important;
            border-radius: 14px !important;
          }
          [style*="maxWidth: '900px'"] > div:first-of-type {
            padding: 16px 14px !important;
          }
          [style*="maxWidth: '900px'"] > div:first-of-type h2 {
            font-size: 18px !important;
            line-height: 1.3 !important;
          }
          [style*="maxWidth: '900px'"] > div:last-of-type {
            padding: 18px 14px !important;
          }
        }
        @media (max-width: 480px) {
          main {
            padding: 14px 12px 20px !important;
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
          main nav {
            margin-bottom: 14px !important;
          }
          main nav a, main nav span {
            font-size: 12px !important;
          }
          main > div[style*="aspectRatio"] {
            max-height: 220px !important;
            margin-bottom: 20px !important;
          }
          main > div[style*="aspectRatio"] > div:last-of-type {
            padding: 18px 12px 22px !important;
          }
          #subservices-section h2 {
            font-size: 20px !important;
          }
          #subservices-section > div > div > div:first-child {
            padding: 14px 12px !important;
          }
          #subservices-section > div > div > div:first-child h3 {
            font-size: 16px !important;
          }
          .service-breadcrumb a,
          .service-breadcrumb span:not(.service-breadcrumb-current) { font-size: 11px !important; }
          .service-breadcrumb-sep { font-size: 11px !important; }
          .service-breadcrumb-current { font-size: 11px !important; }
        }
      `}</style>
    </div>
  );
};

export default ServiceDetailPage;

